import React, { useState, useContext, useEffect, useRef } from "react"
import { View, TouchableOpacity, Animated, ScrollView } from "react-native"
import { ActivityIndicator, Menu, IconButton, TouchableRipple } from "react-native-paper"
import TextM from "@common/components/text/TextM"
import { theme } from "@common/theme/theme"
import { IconImageM } from "@common/components/icons/IconsM"
import FontAwesome5 from "react-native-vector-icons/FontAwesome5"
import FormStepperModal from "@common/components/stepper/FormStepperModal"
import { connect } from "react-redux"
import { apiRequest } from "@data/redux/actions/api"
import { pedagogicalObjectivesService } from "@data/services/pedagogicalObjectivesService"
import { useApiStatus } from "@common/hooks/useApiStatus"
import moment from "moment"
import { DimensionsContext } from "../utils/mobileUtils"
import { SelectInputM } from "../../student/account/onboarding/SelectInputM"
import SchoolSubjectSelector from "@common/components/select/SchoolSubjectSelect"
import FilledButtonM from "@common/components/button/FilledButtonM"
import { weeklyHoursCapacity } from "@common/utils/subscriptionCadence"

const ObjectivesSummaryDisplay = (props) => {
  // ============================================================================
  // PROPS DESTRUCTURING
  // ============================================================================
  const {
    objectives = [],
    schoolSubjects = [],
    objectiveNeeds = [],
    compact = false,
    style = {},
    noHours = false,
    currentLiveSessionId = null,
    hideStatus = false,
    // Admin props
    isAdmin = false,
    studentId = null,
    availableLiveSessions = [],
    // For creation
    user = null,
    hoursPerWeek: hoursPerWeekProp = null,
    scrollable = false,
  } = props

  // ============================================================================
  // STATE MANAGEMENT
  // ============================================================================
  // Edit modal state
  const [editModalVisible, setEditModalVisible] = useState(false)
  const [editingObjective, setEditingObjective] = useState(null)
  const [menuVisible, setMenuVisible] = useState({})
  const [confirmingClose, setConfirmingClose] = useState({})

  // Creation states
  const [draftObjective, setDraftObjective] = useState(null)
  const [isCreating, setIsCreating] = useState(false)
  const [creationAnimValue] = useState(new Animated.Value(0))
  const [creationError, setCreationError] = useState(null)
  const [capacityWarning, setCapacityWarning] = useState(false)
  const aiTimerRef = useRef(null)
  const lastFormulatedSignature = useRef(null)

  // Hour allocation modal state
  const [hourAllocationModalVisible, setHourAllocationModalVisible] = useState(false)

  // ============================================================================
  // DATA TRANSFORMATION
  // ============================================================================
  const subjectOptions = (schoolSubjects || []).map((s) => ({
    id: s?.id,
    label: s?.name,
    value: s?.id,
    icon: s?.icon,
    color: s?.color,
    min_student_class: s?.min_student_class,
  }))

  // Deterministic objective label (type + subjects). We deliberately no longer
  // surface the AI-formulated goal anywhere it is read — it was confusing.
  const buildObjectiveLabel = (objective) => {
    const subj = (objective?.subject_ids || [])
      .map(id => subjectOptions.find(o => o.id === id))
      .filter(Boolean)
    return subj.length > 0
      ? `${objective?.type === 'homework' ? 'Aide aux devoirs' : 'Améliorer son niveau'} en ${subj.map(s => s.label).join(', ')}`
      : 'Objectif pédagogique'
  }

  // ============================================================================
  // CAPACITY CALCULATIONS
  // ============================================================================
  const hoursPerWeek = hoursPerWeekProp ?? weeklyHoursCapacity(user?.subscription_state, 3)

  const usedHours = (objectives || [])
    .filter(o => o.status !== 'closed')
    .reduce((sum, o) => sum + (parseInt(o.allocated_hours) || 0), 0)

  const remaining = Math.max(0, hoursPerWeek - usedHours)
  const isOverCapacity = usedHours > hoursPerWeek
  const isUnderCapacity = usedHours < hoursPerWeek && usedHours > 0

  // ============================================================================
  // UTILITY FUNCTIONS
  // ============================================================================
  const dayAbbr = {
    Monday: 'Lun',
    Tuesday: 'Mar',
    Wednesday: 'Mer',
    Thursday: 'Jeu',
    Friday: 'Ven',
    Saturday: 'Sam',
    Sunday: 'Dim',
  }

  const formatDayTime = (dayTime) => {
    if (!dayTime || typeof dayTime !== 'string') return ''
    const parts = dayTime.split(' ')
    if (parts.length < 2) return dayTime
    const day = dayAbbr[parts[0]] || parts[0]?.slice(0, 3)
    const time = parts[1]
    return `${day} ${time}`
  }

  // ============================================================================
  // OBJECTIVE ACTION HANDLERS (validate, close, edit)
  // ============================================================================
  const handleValidateObjective = (objective) => {
    props.apiRequest(
      pedagogicalObjectivesService.updatePedagogicalObjectiveStatus,
      { studentId, objectiveId: objective.id },
      { status: 'ongoing' }
    )
    setMenuVisible(prev => ({ ...prev, [objective.id]: false }))
  }

  const handleCloseObjective = (objective) => {
    setConfirmingClose(prev => ({ ...prev, [objective.id]: true }))
    setMenuVisible(prev => ({ ...prev, [objective.id]: false }))
  }

  const cancelCloseConfirmation = (objective) => {
    setConfirmingClose(prev => ({ ...prev, [objective.id]: false }))
  }

  const confirmClose = (objective) => {
    props.apiRequest(
      pedagogicalObjectivesService.updatePedagogicalObjective,
      { studentId, objectiveId: objective.id },
      { status: 'closed' }
    )
    setConfirmingClose(prev => ({ ...prev, [objective.id]: false }))
  }

  // ============================================================================
  // CREATION HANDLERS
  // ============================================================================
  const handleAddObjective = () => {
    // Show warning but don't block creation - admins should be free to exceed capacity
    if (remaining <= 0) {
      setCapacityWarning(true)
      setTimeout(() => setCapacityWarning(false), 5000)
    }

    setCapacityWarning(false)
    setCreationError(null)

    const defaultSubject = subjectOptions.find(s =>
      !s.min_student_class ||
      (user?.profile?.class &&
        parseInt(user.profile.class.replace(/[^0-9]/g, '')) >= (s.min_student_class || 0))
    ) || subjectOptions[0]

    const draft = {
      type: 'level_up',
      subject_ids: defaultSubject ? [defaultSubject.id] : [],
      allocated_hours: Math.min(2, Math.max(1, remaining)),
      needs: [],
      status: 'ongoing',
    }

    setDraftObjective(draft)
    setIsCreating(true)

    Animated.timing(creationAnimValue, {
      toValue: 1,
      duration: 300,
      useNativeDriver: false,
    }).start()
  }

  const handleCancelCreation = () => {
    setCreationError(null)

    Animated.timing(creationAnimValue, {
      toValue: 0,
      duration: 300,
      useNativeDriver: false,
    }).start(() => {
      setDraftObjective(null)
      setIsCreating(false)
      lastFormulatedSignature.current = null
    })
  }

  const handleSaveObjective = () => {
    if (!draftObjective || !studentId) return

    const isValid = draftObjective.type && draftObjective.subject_ids?.length > 0 && draftObjective.allocated_hours > 0
    if (!isValid) {
      setCreationError("Veuillez remplir tous les champs obligatoires (type, matière, heures).")
      return
    }

    setCreationError(null)

    const payload = {
      type: draftObjective.type,
      subject_ids: draftObjective.subject_ids,
      allocated_hours: draftObjective.allocated_hours,
      needs: draftObjective.needs || [],
      comment: draftObjective.comment || null,
      formulated_goal: draftObjective.formulated_goal || null,
    }

    props.apiRequest(pedagogicalObjectivesService.createPedagogicalObjective, { studentId }, payload)
  }

  // ============================================================================
  // DRAFT UPDATE HANDLERS
  // ============================================================================
  const updateDraftType = (type) => {
    setDraftObjective(prev => ({
      ...prev,
      type,
      subject_ids: type === 'level_up' ? (prev.subject_ids || []).slice(0, 1) : (prev.subject_ids || []),
    }))
  }

  const updateDraftSubjects = (newSubs) => {
    const ids = newSubs.map(s => s.value)
    setDraftObjective(prev => ({
      ...prev,
      subject_ids: prev.type === 'level_up' ? ids.slice(0, 1) : ids,
    }))
  }

  const updateDraftHours = (direction) => {
    setDraftObjective(prev => {
      const min = Math.max(1, (prev.subject_ids || []).length)
      // No max limit - admins should be free to allocate as many hours as needed
      const cur = parseInt(prev.allocated_hours) || 1
      const newValue = cur + (direction === 'inc' ? 1 : -1)
      return {
        ...prev,
        allocated_hours: Math.max(min, newValue)
      }
    })
  }

  const toggleDraftNeed = (key) => {
    setDraftObjective(prev => {
      const arr = Array.isArray(prev.needs) ? prev.needs.slice() : []
      const found = arr.indexOf(key)
      if (found >= 0) {
        arr.splice(found, 1)
      } else if (arr.length < 2) {
        arr.push(key)
      }
      return { ...prev, needs: arr }
    })
  }

  // ============================================================================
  // EFFECTS & API STATUS HANDLERS
  // ============================================================================
  // AI formulation effect for draft
  useEffect(() => {
    if (aiTimerRef.current) {
      clearTimeout(aiTimerRef.current)
    }

    if (!isCreating || !draftObjective || !studentId) {
      lastFormulatedSignature.current = null
      return
    }

    aiTimerRef.current = setTimeout(() => {
      if (draftObjective?.type && Array.isArray(draftObjective?.subject_ids) && draftObjective.subject_ids.length > 0) {
        const sortedSubjects = [...draftObjective.subject_ids].sort((a, b) => a - b)
        const sortedNeeds = [...(draftObjective.needs || [])].sort()
        const signature = `${draftObjective.type}|${sortedSubjects.join(',')}|${sortedNeeds.join(',')}`

        if (signature !== lastFormulatedSignature.current) {
          lastFormulatedSignature.current = signature
          const payload = {
            type: draftObjective.type,
            subject_ids: draftObjective.subject_ids,
            needs: draftObjective.needs || [],
            comment: draftObjective.comment || null,
          }
          props.apiRequest(pedagogicalObjectivesService.formulateObjectiveGoal, { studentId, objectiveLocalKey: 'draft' }, payload)
        }
      }
    }, 2000)

    return () => {
      if (aiTimerRef.current) clearTimeout(aiTimerRef.current)
    }
  }, [draftObjective, studentId, isCreating])

  // Handle AI formulation success for draft
  useApiStatus(pedagogicalObjectivesService.formulateObjectiveGoal, null, true, (data, _, __, routeParams) => {
    if (routeParams?.objectiveLocalKey === 'draft') {
      const text = data?.data?.formulated_goal || data?.formulated_goal
      if (text) {
        setDraftObjective(prev => prev ? { ...prev, formulated_goal: text } : null)
      }
    }
  })

  // Handle creation success
  useApiStatus(pedagogicalObjectivesService.createPedagogicalObjective, null, true, () => {
    handleCancelCreation()
  })

  // API status for live session updates
  useApiStatus(
    pedagogicalObjectivesService.updateObjectiveLiveSessions,
    null,
    true,
    () => {
      setEditModalVisible(false)
      setEditingObjective(null)
    }
  )

  // API status for objective status updates
  useApiStatus(
    pedagogicalObjectivesService.updatePedagogicalObjectiveStatus,
    null,
    true,
    () => {
      // Success callback - objective should be updated in Redux state
    }
  )

  // API status for objective updates (close)
  useApiStatus(
    pedagogicalObjectivesService.updatePedagogicalObjective,
    null,
    true,
    () => {
      // Success callback - objective should be updated in Redux state
    }
  )

  // ============================================================================
  // RENDER FUNCTIONS - OBJECTIVE ITEM
  // ============================================================================
  const renderObjective = (objective, idx) => {
    const subj = (objective.subject_ids || [])
      .map(id => subjectOptions.find(o => o.id === id)).filter(Boolean)

    const objectiveTitle = buildObjectiveLabel(objective)

    const isActive = !!currentLiveSessionId && Array.isArray(objective?.live_sessions) && objective.live_sessions.some(ls => ls?.id === currentLiveSessionId)
    const isPending = (objective?.status || '').toLowerCase() === 'pending'
    const isOngoing = (objective?.status || '').toLowerCase() === 'ongoing'

    const dimensions = useContext(DimensionsContext)

    return (
      <View key={objective.id || idx} style={{
        width: '100%',
        backgroundColor: isActive ? (theme.colors.primaryLighter || '#EEF6FF') : theme.colors.white,
        borderWidth: isActive ? 2 : 1,
        borderColor: isActive ? (theme.colors.primary || '#2B6CB0') : theme.colors.lightMore,
        borderRadius: 10,
        marginBottom: compact ? 6 : 8,
        paddingVertical: compact ? 8 : 12,
        paddingHorizontal: compact ? 12 : 14,
        opacity: isPending ? 0.5 : 1,
        ...style
      }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', position: 'relative' }}>
          {/* Admin menu or inline confirmation */}
          {isAdmin && (
            <View style={{ position: 'absolute', top: -8, right: -8, zIndex: 10 }}>
              {confirmingClose[objective.id] ? (
                // Inline close confirmation
                <View style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  backgroundColor: theme.colors.errorLight,
                  paddingHorizontal: 8,
                  paddingVertical: 4,
                  borderRadius: 8,
                  borderWidth: 1,
                  borderColor: theme.colors.error
                }}>
                  <TextM fontSize={11} color={theme.colors.error} fontWeight="Medium" style={{ marginRight: 8 }}>
                    Clôturer ?
                  </TextM>
                  <IconButton
                    icon="check"
                    size={14}
                    onPress={() => confirmClose(objective)}
                    style={{
                      margin: 0,
                      minHeight: 28,
                      minWidth: 28,
                      backgroundColor: theme.colors.error
                    }}
                    iconColor={theme.colors.white}
                  />
                  <IconButton
                    icon="times"
                    size={14}
                    onPress={() => cancelCloseConfirmation(objective)}
                    style={{
                      margin: 0,
                      minHeight: 28,
                      minWidth: 28,
                      backgroundColor: theme.colors.white
                    }}
                    iconColor={theme.colors.grey}
                  />
                </View>
              ) : (
                // Normal menu
                <Menu
                  visible={menuVisible[objective.id] || false}
                  onDismiss={() => setMenuVisible(prev => ({ ...prev, [objective.id]: false }))}
                  anchor={
                    <TouchableOpacity
                      onPress={() => setMenuVisible(prev => ({ ...prev, [objective.id]: true }))}
                      style={{
                        padding: 8,
                        backgroundColor: theme.colors.white,
                        borderRadius: 12,
                        shadowColor: '#000',
                        shadowOffset: { width: 0, height: 1 },
                        shadowOpacity: 0.1,
                        shadowRadius: 2,
                        elevation: 2,
                      }}
                    >
                        {((props.updatePedagogicalObjectiveStatusRequest && props.updatePedagogicalObjectiveStatusId === objective.id) ||
                        (props.updatePedagogicalObjectiveRequest && props.updatePedagogicalObjectiveId === objective.id)) ? (
                        <ActivityIndicator size={12} color={theme.colors.primary} />
                      ) : (
                        <FontAwesome5 name="ellipsis-v" size={12} color={theme.colors.grey} />
                      )}
                    </TouchableOpacity>
                  }
                >
                  {isPending && (
                    <Menu.Item
                      onPress={() => handleValidateObjective(objective)}
                      title="Valider"
                      leadingIcon="check"
                    />
                  )}
                  {isOngoing && (
                    <Menu.Item
                      onPress={() => {
                        setEditingObjective(objective)
                        setEditModalVisible(true)
                        setMenuVisible(prev => ({ ...prev, [objective.id]: false }))
                      }}
                      title="Modifier les séances"
                      leadingIcon="edit"
                    />
                  )}
                  {(isPending || isOngoing) && (
                    <Menu.Item
                      onPress={() => handleCloseObjective(objective)}
                      title="Clôturer"
                      leadingIcon="times"
                    />
                  )}
                </Menu>
              )}
            </View>
          )}
          {/* Status badge */}
          {(isPending || isOngoing) && !hideStatus && (
            <View style={{
              position: 'absolute',
              bottom: -2,
              right: -8,
              backgroundColor: isOngoing ? theme.colors.successLight : theme.colors.accentLighter,
              borderRadius: 12,
              paddingHorizontal: 8,
              paddingVertical: 2,
              zIndex: 1,
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}>
              <TextM fontSize={8} color={isOngoing ? theme.colors.success : theme.colors.accentDark} fontWeight="Bold">
                {isOngoing ? 'EN COURS' : 'DEMANDÉ'}
              </TextM>
              {isAdmin && <TextM fontSize={8} color={theme.colors.grey} fontWeight="SemiBold" style={{ marginLeft: 4 }}>
                 - {moment(objective.updated_at).format('DD/MM/YYYY HH:mm')}
              </TextM>}
            </View>
          )}
          {/* Active live session badge */}
          {isActive && (
            <View style={{
              position: 'absolute',
              bottom: -8,
              right: -8,
              backgroundColor: theme.colors.success,
              borderRadius: 12,
              paddingHorizontal: 8,
              paddingVertical: 2,
              zIndex: 1,
              borderWidth: 2,
              borderColor: theme.colors.white,
            }}>
              <TextM fontSize={10} color={theme.colors.white} fontWeight="Bold">
                LIVE EN COURS
              </TextM>
            </View>
          )}

          {/* Subject icons */}
          <View style={{
            width: compact ? 36 : 44,
            height: compact ? 26 : 32,
            marginRight: compact ? 8 : 12,
            flexDirection: 'row',
            alignItems: 'center'
          }}>
            {subj.slice(0, 3).map((s, i) => {

              return(
              <View key={s.id || i} style={{
                marginLeft: i === 0 ? 0 : -20,
                padding: compact ? 3 : 5,
                borderRadius: 20,
                borderWidth: 1,
                borderColor: theme.colors.light,
                backgroundColor: theme.colors.white
              }}>
                <IconImageM
                  uri={s.icon}
                  width={compact ? 14 : 18}
                  style={{}}
                />
              </View>
            )})}
          </View>

          {/* Title and details */}
          <View style={{ flex: 1 }}>
            <TextM
              fontWeight="Medium"
              color={theme.colors.grey}
              wrap
              numberOfLines={2}
              fontSize={compact ? 13 : 14}
            >
              {objectiveTitle}
            </TextM>

            {/* Hours + inline chips + inline needs icons */}
            <View style={{ flexDirection: 'row', alignItems: 'center', marginTop: 2, flexWrap: 'wrap' }}>
              {!noHours && <TextM
                fontSize={11}
                color={theme.colors.primary}
                fontWeight="Medium"
                style={{ marginRight: 12 }}
              >
                {objective.allocated_hours}h/semaine
              </TextM>}
              {/* Inline live session chips — weekly planning of the objective */}
              {Array.isArray(objective?.live_sessions) && objective.live_sessions.length > 0 && (
                <View style={{ flexDirection: 'row', alignItems: 'center', marginLeft: 8, flexWrap: 'wrap' }}>
                  <FontAwesome5
                    name="calendar-alt"
                    size={10}
                    color={theme.colors.grey}
                    style={{ marginRight: 4 }}
                  />
                  {objective.live_sessions.map((ls, i) => (
                    <View key={ls?.id || i} style={{
                      backgroundColor: theme.colors.lighter,
                      borderColor: theme.colors.lightMore,
                      borderWidth: 1,
                      borderRadius: 8,
                      paddingHorizontal: 6,
                      paddingVertical: 2,
                      marginRight: 6,
                    }}>
                      <TextM fontSize={10} color={theme.colors.grey} fontWeight="Medium">
                        {formatDayTime(ls?.day_time)}
                      </TextM>
                    </View>
                  ))}
                </View>
              )}
            </View>

          </View>
        </View>
      </View>
    )
  }

  // ============================================================================
  // RENDER FUNCTIONS - EMPTY STATE
  // ============================================================================
  const renderEmptyState = () => {
    if ((objectives && objectives.length > 0) || isCreating) return null

    return (
      <View style={{
        alignItems: 'center',
        paddingVertical: 20,
      }}>
        <FontAwesome5
          name="graduation-cap"
          size={32}
          color={theme.colors.light}
        />
        <TextM
          color={theme.colors.light}
          style={{ marginTop: 8, textAlign: 'center' }}
        >
          Aucun objectif défini
        </TextM>
      </View>
    )
  }

  // ============================================================================
  // RENDER FUNCTIONS - CREATION FORM
  // ============================================================================
  const renderCreationForm = () => {
    if (!draftObjective || !isAdmin) return null

    return (
      <Animated.View style={{
        width: '100%',
        backgroundColor: theme.colors.white,
        borderWidth: 2,
        borderColor: theme.colors.primary,
        borderRadius: 10,
        marginBottom: 12,
        padding: 14,
        opacity: creationAnimValue,
        transform: [{
          scaleY: creationAnimValue
        }]
      }}>
        {/* Header with save/cancel */}
        <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <TextM fontWeight="Bold" color={theme.colors.primary} fontSize={16}>Nouvel objectif</TextM>
          <View style={{ flexDirection: 'row' }}>
            {props.createObjectiveRequest ? (
              <View style={{ minHeight: 38, minWidth: 38, alignItems: 'center', justifyContent: 'center', borderRadius: 19, backgroundColor: theme.colors.success }}>
                <ActivityIndicator size="small" color={theme.colors.white} />
              </View>
            ) : (
              <>
                <IconButton
                  icon="check"
                  size={20}
                  onPress={handleSaveObjective}
                  style={{ margin: 0, marginRight: 8, minHeight: 38, minWidth: 38 }}
                  iconColor={theme.colors.white}
                  containerColor={theme.colors.success}
                />
                <IconButton
                  icon="times"
                  size={20}
                  onPress={handleCancelCreation}
                  style={{ margin: 0, minHeight: 38, minWidth: 38 }}
                  iconColor={theme.colors.error}
                  containerColor={theme.colors.errorLight}
                />
              </>
            )}
          </View>
        </View>

        {/* Validation error display */}
        {creationError && (
          <View style={{
            backgroundColor: theme.colors.errorLight,
            borderColor: theme.colors.error,
            borderWidth: 1,
            borderRadius: 8,
            padding: 12,
            marginBottom: 12,
            flexDirection: 'row',
            alignItems: 'flex-start'
          }}>
            <FontAwesome5
              name="exclamation-circle"
              size={16}
              color={theme.colors.error}
              style={{ marginRight: 8, marginTop: 2 }}
            />
            <TextM fontSize={13} color={theme.colors.error} style={{ flex: 1 }}>
              {creationError}
            </TextM>
          </View>
        )}

        {/* Type selection and hours */}
        <View style={{ flexDirection: 'row', marginBottom: 16, alignItems: 'flex-end' }}>
          <View style={{ flex: 1, marginRight: 12 }}>
            <SelectInputM
              label="Type d'objectif"
              options={[
                { label: 'Faire ses devoirs', value: 'homework' },
                { label: 'Augmenter son niveau', value: 'level_up' }
              ]}
              value={draftObjective.type}
              onSelect={updateDraftType}
              style={{ minHeight: 44 }}
            />
          </View>

          {/* Hours allocation */}
          <View style={{ alignItems: 'center' }}>
            <TextM fontSize={10} color={theme.colors.primary} fontWeight="Medium" style={{ marginBottom: 4 }}>Heures /sem.</TextM>
            <View style={{ flexDirection: 'row', alignItems: 'center' }}>
              <IconButton
                icon="minus"
                size={16}
                onPress={() => updateDraftHours('dec')}
                style={{
                  minHeight: 36,
                  minWidth: 36,
                  backgroundColor: theme.colors.lighter,
                  borderRadius: 18,
                  margin: 0
                }}
              />
              <View style={{
                marginHorizontal: 8,
                backgroundColor: theme.colors.primary + '20',
                borderColor: theme.colors.primary,
                borderWidth: 1,
                borderRadius: 6,
                paddingVertical: 4,
                paddingHorizontal: 8,
                minWidth: 32,
                alignItems: 'center'
              }}>
                <TextM fontWeight="Bold" fontSize={14} color={theme.colors.primary}>{draftObjective.allocated_hours}</TextM>
              </View>
              <IconButton
                icon="plus"
                size={16}
                onPress={() => updateDraftHours('inc')}
                style={{
                  minHeight: 36,
                  minWidth: 36,
                  backgroundColor: theme.colors.lighter,
                  borderRadius: 18,
                  margin: 0
                }}
              />
            </View>
          </View>
        </View>

        {/* Subject selection */}
        <View style={{ marginBottom: 16 }}>
          <TextM fontSize={12} color={theme.colors.primary} fontWeight="Medium" style={{ marginBottom: 4 }}>
            {draftObjective.type === 'level_up' ? 'Matière' : 'Matières prioritaires'}
          </TextM>
          <SchoolSubjectSelector
            schoolSubjects={subjectOptions.map(s => ({
              label: s.label,
              value: s.id,
              icon: s.icon,
              color: (s.color || '#000') + '15',
              min_student_class: s.min_student_class
            }))}
            subjects={(draftObjective.subject_ids || []).map(id => {
              const found = subjectOptions.find(o => o.id === id)
              return found ? {
                id: found.id,
                value: found.id,
                label: found.label,
                icon: found.icon,
                color: (found.color || '#000') + '15'
              } : { id, value: id, label: String(id) }
            })}
            setSubjects={updateDraftSubjects}
            onSubjectsChange={() => {}}
            maxSelection={draftObjective.type === 'level_up' ? 1 : (draftObjective.allocated_hours || 1) * 2}
            singleSelect={draftObjective.type === 'level_up'}
            studentClass={user?.profile?.class}
            style={{ minHeight: 44 }}
          />
        </View>

        {/* Needs selection */}
        <View style={{ marginBottom: 12 }}>
          <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
            <TextM fontSize={11} color={theme.colors.primary} fontWeight="Medium">Besoins</TextM>
            <TextM fontSize={9} color={theme.colors.light}>
              {(draftObjective.needs || []).length}/2
            </TextM>
          </View>
          <View style={{ flexDirection: 'row', flexWrap: 'wrap' }}>
            {(objectiveNeeds || []).map(n => {
              const active = (draftObjective.needs || []).includes(n.key)
              const disabled = !active && (draftObjective.needs || []).length >= 2
              return (
                <TouchableRipple
                  key={n.key}
                  onPress={() => !disabled && toggleDraftNeed(n.key)}
                  style={{
                    marginRight: 4,
                    marginBottom: 4,
                    minHeight: 32,
                    minWidth: 32,
                    justifyContent: 'center'
                  }}
                  disabled={disabled}
                >
                  <View style={{
                    backgroundColor: active ? theme.colors.primaryLighter : disabled ? theme.colors.lighter : theme.colors.lighter,
                    borderColor: active ? theme.colors.primary : disabled ? theme.colors.lightMore : theme.colors.lightMore,
                    borderWidth: 1,
                    paddingVertical: 4,
                    paddingHorizontal: 8,
                    borderRadius: 12,
                    flexDirection: 'row',
                    alignItems: 'center',
                    opacity: disabled ? 0.5 : 1
                  }}>
                    <FontAwesome5 name={n.icon} size={9} color={active ? theme.colors.primary : theme.colors.grey} style={{ marginRight: 4 }} />
                    <TextM fontSize={10} color={active ? theme.colors.primary : theme.colors.grey}>{n.label}</TextM>
                  </View>
                </TouchableRipple>
              )
            })}
          </View>
        </View>

      </Animated.View>
    )
  }

  // ============================================================================
  // RENDER FUNCTIONS - CAPACITY WARNINGS
  // ============================================================================
  const renderCapacityWarnings = () => (
    <>
      {/* Over-capacity warning (persistent, always visible when exceeded) */}
      {isOverCapacity && isAdmin && (
        <View style={{
          backgroundColor: theme.colors.errorLight,
          borderColor: theme.colors.error,
          borderWidth: 1,
          borderRadius: 8,
          padding: 12,
          marginBottom: 12,
          flexDirection: 'row',
          alignItems: 'flex-start'
        }}>
          <FontAwesome5
            name="exclamation-circle"
            size={16}
            color={theme.colors.error}
            style={{ marginRight: 8, marginTop: 2 }}
          />
          <View style={{ flex: 1 }}>
            <TextM fontSize={13} color={theme.colors.error} fontWeight="Bold" style={{ marginBottom: 4 }}>
              Dépassement de capacité
            </TextM>
            <TextM fontSize={12} color={theme.colors.error}>
              Le nombre d'heures allouées ({usedHours}h/semaine) dépasse la capacité théorique de l'élève ({hoursPerWeek}h/semaine).
            </TextM>
          </View>
        </View>
      )}

      {/* Under-capacity info (persistent, shown when hours not fully allocated) */}
      {isUnderCapacity && isAdmin && (
        <View style={{
          backgroundColor: theme.colors.primaryLighter,
          borderColor: theme.colors.primary,
          borderWidth: 1,
          borderRadius: 8,
          padding: 12,
          marginBottom: 12,
          flexDirection: 'row',
          alignItems: 'flex-start'
        }}>
          <FontAwesome5
            name="info-circle"
            size={16}
            color={theme.colors.primary}
            style={{ marginRight: 8, marginTop: 2 }}
          />
          <View style={{ flex: 1 }}>
            <TextM fontSize={13} color={theme.colors.primary} fontWeight="Bold" style={{ marginBottom: 4 }}>
              Heures restantes à allouer
            </TextM>
            <TextM fontSize={12} color={theme.colors.primary}>
              {remaining}h/semaine restante{remaining > 1 ? 's' : ''} à allouer sur {hoursPerWeek}h/semaine disponibles. Merci d'allouer toutes les heures.
            </TextM>
          </View>
        </View>
      )}

      {/* Capacity warning (inline, for modal compatibility) */}
      {capacityWarning && isAdmin && (
        <View style={{
          backgroundColor: theme.colors.warningLight,
          borderColor: theme.colors.warning,
          borderWidth: 1,
          borderRadius: 8,
          padding: 12,
          marginBottom: 12,
          flexDirection: 'row',
          alignItems: 'flex-start'
        }}>
          <FontAwesome5
            name="exclamation-triangle"
            size={16}
            color={theme.colors.warning}
            style={{ marginRight: 8, marginTop: 2 }}
          />
          <View style={{ flex: 1 }}>
            <TextM fontSize={13} color={theme.colors.warning} fontWeight="Bold" style={{ marginBottom: 4 }}>
              Capacité maximale atteinte
            </TextM>
            <TextM fontSize={12} color={theme.colors.warning}>
              Toutes les heures disponibles ({hoursPerWeek}h/semaine) sont déjà allouées.
            </TextM>
          </View>
        </View>
      )}
    </>
  )

  // ============================================================================
  // MODAL FORM DATA
  // ============================================================================
  const modalFormData = editingObjective ? [
    {
      label: `${buildObjectiveLabel(editingObjective)} (${editingObjective.allocated_hours}h/semaine)`,
      name: "live_session_ids",
      type: "select.multiple",
      maxSelection: editingObjective.allocated_hours,
      options: availableLiveSessions.map(ls => ({
        label: formatDayTime(ls.day_time) + ' - ' + ls.tutor?.firstname + ' ' + ls.tutor?.lastname,
        value: ls.id,
        disabled: objectives?.filter(o => o.id !== editingObjective.id)?.some(o => o.live_sessions?.some(l => l.id === ls.id)),
      })),
      defaultValue: editingObjective.live_sessions?.map(ls => ls.id) || [],
      description: "Sélectionnez les séances où cet objectif sera travaillé",
      showNextButton: true
    }
  ] : []

  // Hour allocation modal form data
  const hourAllocationFormData = [
    {
      label: "Répartition des heures",
      type: "custom",
      name: "hour_allocation",
      showNextButton: true,
      component: ({ field, setFormValues }) => {
        const activeObjectives = (objectives || []).filter(o => o.status !== 'closed')
        const allocation = field.value || {}

        const setAllocation = (newAllocation) => {
          field.onChange?.(newAllocation)
          setFormValues((prev) => ({
            ...prev,
            [field.name]: newAllocation,
          }))
        }

        // Initialize allocation from objectives
        useEffect(() => {
          if (!field.value || Object.keys(field.value).length === 0) {
            const initialAllocation = {}
            activeObjectives.forEach(obj => {
              initialAllocation[`hours_${obj.id}`] = obj.allocated_hours
            })
            setAllocation(initialAllocation)
          }
        }, [activeObjectives.length])

        const updateObjectiveHours = (objectiveId, direction) => {
          const key = `hours_${objectiveId}`
          // Find the objective to get its actual allocated hours as fallback
          const objective = activeObjectives.find(obj => obj.id === objectiveId)
          const currentHours = allocation[key] !== undefined ? allocation[key] : (objective?.allocated_hours || 1)
          const newHours = direction === 'inc' ? currentHours + 1 : currentHours - 1

          // Simple validation: ensure hours >= 1
          if (newHours < 1) {
            return
          }

          setAllocation({
            ...allocation,
            [key]: newHours,
          })
        }

        // Calculate total from allocation (with fallback to objective values)
        const totalAllocated = activeObjectives.reduce((sum, obj) => {
          const key = `hours_${obj.id}`
          const hours = allocation[key] !== undefined ? allocation[key] : obj.allocated_hours
          return sum + (hours || 0)
        }, 0)

        return (
          <ScrollView style={{ width: '100%' }} contentContainerStyle={{ paddingBottom: 100 }}>
            {activeObjectives.map(objective => {
              const key = `hours_${objective.id}`
              const hours = allocation[key] !== undefined ? allocation[key] : objective.allocated_hours
              const subj = (objective.subject_ids || [])
                .map(id => subjectOptions.find(o => o.id === id))
                .filter(Boolean)

              // Simple constraints: only ensure minimum 1h per objective
              const canDecrement = hours > 1
              const canIncrement = true // No upper limit, allow flexibility

              return (
                <View key={objective.id} style={{
                  backgroundColor: theme.colors.white,
                  borderWidth: 1,
                  borderColor: theme.colors.lightMore,
                  borderRadius: 12,
                  padding: 16,
                  marginBottom: 12,
                  flexDirection: 'row',
                  alignItems: 'center',
                }}>
                  {/* Objective Info */}
                  <View style={{ flex: 1, marginRight: 12 }}>
                    <TextM fontWeight="Medium" fontSize={14} numberOfLines={2}>
                      {buildObjectiveLabel(objective)}
                    </TextM>
                    {subj.length > 0 && (
                      <View style={{ flexDirection: 'row', alignItems: 'center', marginTop: 4 }}>
                        {subj.slice(0, 2).map((s, i) => (
                          <View key={s.id} style={{
                            marginRight: 6,
                            padding: 4,
                            borderRadius: 12,
                            backgroundColor: (s.color || '#000') + '15',
                          }}>
                            <TextM fontSize={10} color={theme.colors.grey}>{s.label}</TextM>
                          </View>
                        ))}
                      </View>
                    )}
                  </View>

                  {/* Hour Controls */}
                  <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                    <IconButton
                      icon="minus"
                      size={16}
                      disabled={!canDecrement}
                      onPress={() => updateObjectiveHours(objective.id, 'dec')}
                      style={{
                        backgroundColor: !canDecrement ? theme.colors.lighter : theme.colors.primaryLighter,
                        margin: 0,
                        width: 32,
                        height: 32,
                      }}
                      iconColor={!canDecrement ? theme.colors.light : theme.colors.primary}
                    />
                    <View style={{
                      backgroundColor: theme.colors.primaryLighter,
                      borderColor: theme.colors.primary,
                      borderWidth: 1,
                      borderRadius: 6,
                      paddingVertical: 4,
                      paddingHorizontal: 12,
                      marginHorizontal: 8,
                      minWidth: 40,
                      alignItems: 'center',
                    }}>
                      <TextM fontWeight="Bold" fontSize={16} color={theme.colors.primary}>
                        {hours}
                      </TextM>
                    </View>
                    <IconButton
                      icon="plus"
                      size={16}
                      disabled={!canIncrement}
                      onPress={() => updateObjectiveHours(objective.id, 'inc')}
                      style={{
                        backgroundColor: !canIncrement ? theme.colors.lighter : theme.colors.primaryLighter,
                        margin: 0,
                        width: 32,
                        height: 32,
                      }}
                      iconColor={!canIncrement ? theme.colors.light : theme.colors.primary}
                    />
                  </View>
                </View>
              )
            })}

            {/* Total Display */}
            <View style={{
              backgroundColor: totalAllocated === hoursPerWeek
                ? theme.colors.successLight
                : totalAllocated > hoursPerWeek
                  ? theme.colors.errorLight
                  : theme.colors.warningLight,
              borderWidth: 1,
              borderColor: totalAllocated === hoursPerWeek
                ? theme.colors.success
                : totalAllocated > hoursPerWeek
                  ? theme.colors.error
                  : theme.colors.warning,
              borderRadius: 12,
              padding: 16,
              marginTop: 8,
              alignItems: 'center',
            }}>
              <TextM fontWeight="Bold" fontSize={16} color={
                totalAllocated === hoursPerWeek
                  ? theme.colors.success
                  : totalAllocated > hoursPerWeek
                    ? theme.colors.error
                    : theme.colors.warning
              }>
                Total: {totalAllocated}/{hoursPerWeek} heures
              </TextM>
              {totalAllocated > hoursPerWeek && (
                <TextM fontSize={12} color={theme.colors.error} style={{ marginTop: 4, textAlign: 'center' }}>
                  Dépassement de {totalAllocated - hoursPerWeek}h
                </TextM>
              )}
              {totalAllocated < hoursPerWeek && (
                <TextM fontSize={12} color={theme.colors.warning} style={{ marginTop: 4, textAlign: 'center' }}>
                  {hoursPerWeek - totalAllocated}h restante{hoursPerWeek - totalAllocated > 1 ? 's' : ''}
                </TextM>
              )}
            </View>
          </ScrollView>
        )
      }
    }
  ]

  // ============================================================================
  // MAIN CONTENT ASSEMBLY
  // ============================================================================
  const content = (
    <View style={{ width: '100%', ...style }}>
      {/* Existing objectives */}
      {objectives.map((objective, idx) => renderObjective(objective, idx))}

      {/* Empty state */}
      {renderEmptyState()}

      {/* Capacity warnings */}
      {renderCapacityWarnings()}

      {/* Creation form */}
      {renderCreationForm()}

      {/* Add button and hour allocation button */}
      {isAdmin && !isCreating && (
        <View style={{ flexDirection: 'row', marginTop: 12, gap: 8 }}>
          <FilledButtonM
            onPress={handleAddObjective}
            mode="outlined"
            icon="plus"
            label="Ajouter un objectif"
            style={{ flex: 1 }}
          />
          {objectives && objectives.length > 0 && (
            <FilledButtonM
              onPress={() => setHourAllocationModalVisible(true)}
              mode="outlined"
              icon="clock"
              label="Répartir les heures"
              style={{ flex: 1 }}
            />
          )}
        </View>
      )}
    </View>
  )

  // ============================================================================
  // MAIN RETURN
  // ============================================================================
  return (
    <>
      {scrollable ? (
        <ScrollView style={{ width: '100%' }} contentContainerStyle={{ paddingBottom: 100 }}>
          {content}
        </ScrollView>
      ) : (
        content
      )}

      {/* Edit modal for admin */}
      <FormStepperModal
        visible={editModalVisible}
        setVisible={() => {
          setEditModalVisible(false)
          setEditingObjective(null)
        }}
        formData={modalFormData}
        submitButtonText="Mettre à jour"
        title="Modifier les séances"
        submitService={pedagogicalObjectivesService.updateObjectiveLiveSessions}
        submitRouteParams={{ studentId, objectiveId: editingObjective?.id }}
      />

      {/* Hour allocation modal for admin */}
      <FormStepperModal
        visible={hourAllocationModalVisible}
        setVisible={setHourAllocationModalVisible}
        formData={hourAllocationFormData}
        submitButtonText="Mettre à jour"
        title="Modifier l'allocation des heures"
        submitService={pedagogicalObjectivesService.updateObjectivesHourAllocation}
        submitRouteParams={{ studentId }}
      />
    </>
  )
}

// ============================================================================
// REDUX CONNECTION
// ============================================================================
const mapStateToProps = (state) => ({
  updatePedagogicalObjectiveStatusRequest: state.api.updatePedagogicalObjectiveStatus?.request,
  updatePedagogicalObjectiveStatusId: state.api.updatePedagogicalObjectiveStatus?.routeParams?.objectiveId,
  updatePedagogicalObjectiveRequest: state.api.updatePedagogicalObjective?.request,
  updatePedagogicalObjectiveId: state.api.updatePedagogicalObjective?.routeParams?.objectiveId,
  createObjectiveRequest: state.api.createPedagogicalObjective?.request,
})

const mapDispatchToProps = {
  apiRequest,
}

export default connect(mapStateToProps, mapDispatchToProps)(ObjectivesSummaryDisplay)
